import { Avatar } from '@commenda/kakashi';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useState, type FocusEvent, type KeyboardEvent } from 'react';

import { useConfig } from 'context/useConfig';
import { useAccountManagerCallEnabled } from 'hooks/useAccountManagerCallEnabled';

import { AccountManagerCard } from './AccountManagerCard';
import { InboxPillButton } from './InboxPillButton';
import { useAccountManager } from './useAccountManager';

const ACCOUNT_MANAGER_TITLE = 'Customer Success Manager';

const EASE_OUT = [0.23, 1, 0.32, 1] as const;

interface AccountManagerMenuProps {
  isInboxOpen: boolean;
  onInboxToggle: () => void;
}

export const AccountManagerMenu = ({
  isInboxOpen,
  onInboxToggle,
}: AccountManagerMenuProps) => {
  const { companyId } = useConfig();
  const { enabled: isCallEnabled } = useAccountManagerCallEnabled(companyId);
  const { accountManager } = useAccountManager();
  const { name, email, image, meetingCalendarLink } = accountManager;

  const prefersReducedMotion = useReducedMotion();
  const [isRevealed, setIsRevealed] = useState(false);

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsRevealed(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') setIsRevealed(false);
  };

  return (
    <div
      className="relative flex items-center justify-end"
      onMouseEnter={() => setIsRevealed(true)}
      onMouseLeave={() => setIsRevealed(false)}
      onFocus={() => setIsRevealed(true)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center gap-md rounded-full px-sm py-xs">
        <button
          type="button"
          onClick={() => setIsRevealed(true)}
          aria-label={`Account manager: ${name}. Open menu`}
          aria-expanded={isRevealed}
          className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-fg-tertiary"
        >
          <Avatar
            size="xs"
            name={name}
            image={image}
            className="pointer-events-none shrink-0"
            colorSeed={name}
          />
        </button>

        <InboxPillButton
          isInboxOpen={isInboxOpen}
          onInboxToggle={onInboxToggle}
        />
      </div>

      <AnimatePresence>
        {isRevealed && (
          <motion.div
            className="absolute right-0 top-full z-50 pt-xs"
            style={{ transformOrigin: 'top right' }}
            initial={
              prefersReducedMotion
                ? { opacity: 0 }
                : { opacity: 0, y: -6, scale: 0.97 }
            }
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: { duration: 0.2, ease: EASE_OUT },
            }}
            exit={
              prefersReducedMotion
                ? { opacity: 0, transition: { duration: 0.12 } }
                : {
                    opacity: 0,
                    y: -4,
                    scale: 0.98,
                    transition: { duration: 0.13, ease: EASE_OUT },
                  }
            }
          >
            <AccountManagerCard
              name={name}
              title={ACCOUNT_MANAGER_TITLE}
              email={email}
              image={image}
              meetingCalendarLink={
                isCallEnabled ? meetingCalendarLink : undefined
              }
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
